import { useCallback, useEffect, useState } from 'react';
import api from '../config/api';

function distanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Start/stop trips and keep a live track polyline in sync with saved trip_tracks.
 */
export function useTripRecorder(vesselId, location) {
  const [activeTrip, setActiveTrip] = useState(null);
  const [trackPoints, setTrackPoints] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadTracks = useCallback(async (tripId) => {
    try {
      const res = await api.get(`/api/trips/${tripId}?include_tracks=true`);
      const tracks = res.data?.tracks || [];
      const pts = tracks
        .filter((t) => Number.isFinite(t.latitude) && Number.isFinite(t.longitude))
        .map((t) => [t.latitude, t.longitude]);
      if (pts.length) setTrackPoints(pts);
    } catch (err) {
      console.error('Failed to load trip tracks:', err);
    }
  }, []);

  const refreshActive = useCallback(async () => {
    if (!vesselId) return;
    try {
      const res = await api.get('/api/trips/active/list');
      const trips = res.data?.active_trips || [];
      const trip = trips.find((t) => t.vessel_id === vesselId) || null;
      setActiveTrip(trip);
      if (trip) await loadTracks(trip.id);
      else setTrackPoints([]);
    } catch (err) {
      console.error('Failed to load active trips:', err);
    }
  }, [vesselId, loadTracks]);

  useEffect(() => {
    refreshActive();
    const timer = setInterval(refreshActive, 15000);
    return () => clearInterval(timer);
  }, [refreshActive]);

  useEffect(() => {
    if (!activeTrip?.id) return undefined;
    const timer = setInterval(() => loadTracks(activeTrip.id), 30000);
    return () => clearInterval(timer);
  }, [activeTrip?.id, loadTracks]);

  useEffect(() => {
    if (!activeTrip || !Number.isFinite(location?.latitude) || !Number.isFinite(location?.longitude)) {
      return;
    }
    setTrackPoints((prev) => {
      if (!prev.length) return [[location.latitude, location.longitude]];
      const [lat, lng] = prev[prev.length - 1];
      if (distanceMeters(lat, lng, location.latitude, location.longitude) < 15) return prev;
      return [...prev, [location.latitude, location.longitude]];
    });
  }, [activeTrip, location?.latitude, location?.longitude]);

  const startTrip = useCallback(async () => {
    if (!vesselId || busy) return null;
    setBusy(true);
    try {
      const now = new Date();
      const tripName = `Trip ${now.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })} ${now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
      const res = await api.post('/api/trips', {
        vessel_id: vesselId,
        trip_name: tripName,
        start_latitude: location?.latitude ?? null,
        start_longitude: location?.longitude ?? null,
      });
      setActiveTrip(res.data.trip);
      setTrackPoints(
        Number.isFinite(location?.latitude)
          ? [[location.latitude, location.longitude]]
          : []
      );
      return res.data.trip;
    } finally {
      setBusy(false);
    }
  }, [vesselId, location, busy]);

  const stopTrip = useCallback(async () => {
    if (!activeTrip || busy) return null;
    setBusy(true);
    try {
      const res = await api.put(`/api/trips/${activeTrip.id}`, {
        trip_status: 'completed',
        end_latitude: location?.latitude ?? null,
        end_longitude: location?.longitude ?? null,
      });
      const completed = res.data.trip;
      setActiveTrip(null);
      setTrackPoints([]);
      return completed;
    } finally {
      setBusy(false);
    }
  }, [activeTrip, location, busy]);

  return {
    activeTrip,
    trackPoints,
    recording: Boolean(activeTrip),
    busy,
    startTrip,
    stopTrip,
    refreshActive,
  };
}
